import { useEffect, useState } from "react";
import { Form, Modal, Select, Spin, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "react-query";
import { AcademicUser } from "../../Api";
import { theme } from "../../theme";
import useDebounce from "../../Hooks/debounce";
import { getAllAcademicUsers } from "../../Requests/academic-user-requests";
import { assignSubjectCoordinators, getShortSubject } from "../../Requests/subject-requests";
import { NOTIFICATION_TYPES, openNotification } from "../Notifications/NotificationsUtils";

const { Option } = Select;

interface SubjectCoordinatorsModalProps {
    subjectId?: string;
    subjectName?: string | null;
    onClose: () => void;
}

interface CoordinatorsFormValues {
    courseCoordinatorIds: string[];
    labCoordinatorIds: string[];
}

// Asignarea titularilor e separata de fisa: functioneaza indiferent de statusul planului sau al fisei.
// Cine o poate face decide backend-ul (SubjectController.AssignCoordinators).
const SubjectCoordinatorsModal = ({ subjectId, subjectName, onClose }: SubjectCoordinatorsModalProps) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [form] = Form.useForm<CoordinatorsFormValues>();
    const [saving, setSaving] = useState(false);

    const [courseSearchTerm, setCourseSearchTerm] = useState('');
    const debouncedCourseSearch = useDebounce(courseSearchTerm, 1000);
    const [labSearchTerm, setLabSearchTerm] = useState('');
    const debouncedLabSearch = useDebounce(labSearchTerm, 1000);

    const open = !!subjectId;

    const openGetErrorNotification = () => {
        openNotification('Eroare', 'Nu s-au putut prelua datele de pe server!', NOTIFICATION_TYPES.ERROR);
    };

    // Datele proaspete ale disciplinei (titularii curenti), nu cele din lista
    const { data: subject, isLoading: isLoadingSubject } = useQuery(
        ['getSubjectCoordinators', subjectId],
        () => getShortSubject(subjectId!),
        { enabled: open, refetchOnWindowFocus: false, cacheTime: 0, onError: openGetErrorNotification }
    );

    const { data: courseUsers, isLoading: isLoadingCourseSearch } = useQuery(
        ['getAcademiUsersCourse', debouncedCourseSearch],
        () => getAllAcademicUsers(debouncedCourseSearch, 1, 10, undefined),
        { enabled: open, onError: openGetErrorNotification }
    );

    const { data: labUsers, isLoading: isLoadingLabSearch } = useQuery(
        ['getAcademiUsersLab', debouncedLabSearch],
        () => getAllAcademicUsers(debouncedLabSearch, 1, 10, undefined),
        { enabled: open, onError: openGetErrorNotification }
    );

    useEffect(() => {
        if (open && subject) {
            form.setFieldsValue({
                courseCoordinatorIds: subject.courseCoordinators?.map((user: AcademicUser) => user.id || '') || [],
                labCoordinatorIds: subject.labCoordinators?.map((user: AcademicUser) => user.id || '') || [],
            });
        }
    }, [open, subject, form]);

    const handleClose = () => {
        form.resetFields();
        setCourseSearchTerm('');
        setLabSearchTerm('');
        onClose();
    };

    const handleOk = async () => {
        if (!subjectId) return;

        const values = form.getFieldsValue();
        setSaving(true);
        try {
            await assignSubjectCoordinators(subjectId, values.courseCoordinatorIds ?? [], values.labCoordinatorIds ?? []);
            openNotification('Succes', 'Titularii au fost actualizați.', NOTIFICATION_TYPES.SUCCESS);
            queryClient.invalidateQueries('getSubjects');
            queryClient.invalidateQueries(['getSubject', subjectId]);
            queryClient.invalidateQueries(['getSubjectCoordinators', subjectId]);
            handleClose();
        } catch (err: any) {
            const message = err?.status === 403
                ? 'Nu aveți drepturi pentru a modifica titularii acestei discipline.'
                : (await err?.text?.().catch(() => '')) || 'Titularii nu au putut fi actualizați.';
            openNotification('Eroare', message, NOTIFICATION_TYPES.ERROR);
        } finally {
            setSaving(false);
        }
    };

    // Titularii curenti raman in optiuni chiar daca nu apar in rezultatele cautarii
    type UserOption = { id?: string; name?: string | null; email?: string | null };
    const buildOptions = (current: UserOption[] | null | undefined, searched: UserOption[] | null | undefined) =>
        [...(current || []), ...(searched || []).filter(user => !(current || []).some(u => u.id === user.id))]
            .map(user => (
                <Option key={user.id} value={user.id}>
                    {user.name} - {user.email}
                </Option>
            ));

    return (
        <Modal
            open={open}
            onOk={handleOk}
            onCancel={handleClose}
            confirmLoading={saving}
            title={<div style={{ paddingRight: '24px' }}>Asignare titulari{subjectName ? ` - ${subjectName}` : ''}</div>}
            width="600px"
            okText={t('account.confirm')}
            cancelText={t('account.cancel')}
            okButtonProps={{
                disabled: isLoadingSubject,
                style: { background: theme.secondColor, border: theme.secondColor, borderRadius: '10px' }
            }}
            cancelButtonProps={{
                style: { background: theme.green, border: theme.green, color: theme.white, borderRadius: '10px' }
            }}
        >
            <Spin spinning={isLoadingSubject}>
            <Form form={form} layout="vertical">
                <Typography.Text strong>Titularul activităților de curs</Typography.Text>
                <Form.Item name="courseCoordinatorIds">
                    <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        filterOption={false}
                        maxTagCount={10}
                        style={{ width: "100%", textAlign: "left" }}
                        placeholder="Căutare titular curs"
                        onSearch={setCourseSearchTerm}
                        onSelect={() => setCourseSearchTerm('')}
                        notFoundContent={isLoadingCourseSearch ? <Spin size="small" /> : null}
                    >
                        {buildOptions(subject?.courseCoordinators, courseUsers?.data)}
                    </Select>
                </Form.Item>

                <Typography.Text strong>Titularul activităților de seminar/ laborator/ proiect</Typography.Text>
                <Form.Item name="labCoordinatorIds">
                    <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        filterOption={false}
                        maxTagCount={10}
                        style={{ width: "100%", textAlign: "left" }}
                        placeholder="Căutare titular seminar/laborator/proiect"
                        onSearch={setLabSearchTerm}
                        onSelect={() => setLabSearchTerm('')}
                        notFoundContent={isLoadingLabSearch ? <Spin size="small" /> : null}
                    >
                        {buildOptions(subject?.labCoordinators, labUsers?.data)}
                    </Select>
                </Form.Item>
            </Form>
            </Spin>
        </Modal>
    );
};

export default SubjectCoordinatorsModal;
